// C1 - Ünite 1: Perfect Tenses Advanced
// Hedef: Present Perfect Continuous, Past Perfect, Future Perfect

export const C1_UNIT_1 = {
  'C1-U1-L1': {
    id: 'C1-U1-L1',
    title: 'Present Perfect Continuous',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Present Perfect Continuous',
          explanation: 'Geçmişte başlayıp hâlâ devam eden veya yakın zamanda bitmiş eylemleri vurgular. Have/Has + been + V-ing',
          table: {
            headers: ['Yapı', 'Örnek', 'Kullanım'],
            rows: [
              ['I/You/We/They have been + V-ing', 'I have been working here for 5 years.', 'Süregelen eylem'],
              ['He/She/It has been + V-ing', 'She has been flying since 2018.', 'Deneyim süresi'],
              ['Olumsuz: have/has not been + V-ing', 'I haven\'t been sleeping well lately.', 'Son zamanlarda'],
              ['Soru: Have/Has + S + been + V-ing?', 'How long have you been waiting?', 'Ne zamandır?']
            ]
          }
        },
        {
          type: 'tip',
          content: '💡 Present Perfect Continuous eylemin süresini ve devam ettiğini vurgular. "I have been working" = Çalışıyorum ve hâlâ devam ediyor.'
        },
        {
          type: 'vocabulary',
          title: '✈️ Havacılık Örnekleri',
          words: [
            { en: 'I have been flying for 10 hours.', tr: '10 saattir uçuyorum.' },
            { en: 'We have been experiencing turbulence.', tr: 'Türbülans yaşıyoruz (bir süredir).' },
            { en: 'Passengers have been waiting at the gate.', tr: 'Yolcular kapıda bekliyorlar.' },
            { en: 'The crew has been serving meals.', tr: 'Ekip yemek servisi yapıyor.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'swipeVocabulary',
          title: '🎴 Present Perfect Continuous - Biliyor musun?',
          cards: [
            { front: 'have been working', back: 'çalışıyorum (süregelen)', example: 'I have been working here for 5 years.' },
            { front: 'has been flying', back: 'uçuyor (süregelen)', example: 'She has been flying since 2018.' },
            { front: 'have been waiting', back: 'bekliyorum (süregelen)', example: 'We have been waiting for an hour.' },
            { front: 'has been serving', back: 'servis yapıyor', example: 'The crew has been serving drinks.' },
            { front: 'have been experiencing', back: 'yaşıyoruz', example: 'We have been experiencing delays.' },
            { front: 'How long have you been...?', back: 'Ne zamandır...?', example: 'How long have you been working here?' }
          ]
        },
        {
          type: 'multipleChoice',
          question: 'She ___ as a cabin crew member for 10 years.',
          options: ['has worked', 'has been working', 'is working', 'works'],
          correctIndex: 1,
          explanation: 'Süregelen eylem için "has been working" kullanılır.'
        },
        {
          type: 'fillBlank',
          sentences: [
            { text: 'I ___ ___ ___ (wait) here for 30 minutes.', answer: 'have been waiting' },
            { text: 'How long ___ you ___ ___ (fly)?', answer: 'have been flying' }
          ]
        }
      ]
    }
  },

  'C1-U1-L2': {
    id: 'C1-U1-L2',
    title: 'Past Perfect',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Past Perfect Tense',
          explanation: 'Geçmişteki bir olaydan önce tamamlanmış başka bir eylemi anlatır. Had + V3 (past participle)',
          table: {
            headers: ['Yapı', 'Örnek', 'Anlam'],
            rows: [
              ['Had + V3', 'The plane had landed before the storm.', 'Fırtınadan ÖNCE inmişti'],
              ['Had not + V3', 'I hadn\'t finished the safety check.', 'Tamamlamamıştım'],
              ['Had + S + V3?', 'Had you worked there before?', 'Daha önce çalışmış mıydın?'],
              ['After/Before/When + Past Perfect', 'After we had boarded, the captain spoke.', 'Bindikten sonra']
            ]
          }
        },
        {
          type: 'example',
          title: '✈️ Uçuş Senaryoları',
          items: [
            { en: 'By the time we arrived, the flight had departed.', tr: 'Vardığımızda uçuş kalkmıştı.' },
            { en: 'The passenger had already eaten before the meal service.', tr: 'Yolcu servis öncesi yemişti.' },
            { en: 'I had never flown business class until that day.', tr: 'O güne kadar hiç business uçmamıştım.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'dragDrop',
          instruction: 'Cümleyi doğru sıraya koyun',
          words: ['had', 'The', 'before', 'landed', 'plane', 'arrived', 'we'],
          correctOrder: ['The', 'plane', 'had', 'landed', 'before', 'we', 'arrived'],
          translation: 'Biz varmadan önce uçak inmişti.'
        },
        {
          type: 'multipleChoice',
          question: 'By the time we boarded, the crew ___ the safety equipment.',
          options: ['checked', 'had checked', 'have checked', 'was checking'],
          correctIndex: 1,
          explanation: 'Geçmişteki olaydan önce tamamlanan eylem için Past Perfect kullanılır.'
        }
      ]
    }
  },

  'C1-U1-L3': {
    id: 'C1-U1-L3',
    title: 'Past Perfect Continuous',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Past Perfect Continuous',
          explanation: 'Geçmişteki bir noktaya kadar süregelen eylemi anlatır. Had been + V-ing',
          table: {
            headers: ['Yapı', 'Örnek', 'Kullanım'],
            rows: [
              ['Had been + V-ing', 'I had been working for 8 hours when the shift ended.', 'Süre vurgusu'],
              ['Had not been + V-ing', 'She hadn\'t been feeling well before the flight.', 'Olumsuz süreç'],
              ['How long had + S + been + V-ing?', 'How long had you been waiting?', 'Ne kadar süredir?']
            ]
          }
        },
        {
          type: 'tip',
          content: '💡 Past Perfect Continuous = "had been + V-ing". Geçmişteki bir ana kadar devam eden süreç.'
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'The passengers ___ for two hours when the flight was finally announced.',
          options: ['waited', 'were waiting', 'had been waiting', 'have been waiting'],
          correctIndex: 2,
          explanation: 'Geçmişteki ana kadar süregelen eylem için "had been waiting" kullanılır.'
        }
      ]
    }
  },

  'C1-U1-L4': {
    id: 'C1-U1-L4',
    title: 'Future Perfect',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Future Perfect Tense',
          explanation: 'Gelecekteki belirli bir zamana kadar tamamlanmış olacak eylemleri anlatır. Will have + V3',
          table: {
            headers: ['Yapı', 'Örnek', 'Anlam'],
            rows: [
              ['Will have + V3', 'By 6 PM, we will have landed.', 'Saat 6\'ya kadar inmiş olacağız'],
              ['Will not have + V3', 'We won\'t have finished by then.', 'O zamana kadar bitirmemiş olacağız'],
              ['Will + S + have + V3?', 'Will you have completed the training?', 'Eğitimi tamamlamış olacak mısın?']
            ]
          }
        },
        {
          type: 'vocabulary',
          title: '✈️ Havacılık Örnekleri',
          words: [
            { en: 'By midnight, we will have served 300 passengers.', tr: 'Gece yarısına kadar 300 yolcuya servis yapmış olacağız.' },
            { en: 'The flight will have arrived by 10 AM.', tr: 'Uçuş saat 10\'a kadar varmış olacak.' },
            { en: 'I will have worked 1000 flight hours by next year.', tr: 'Gelecek yıla kadar 1000 uçuş saati çalışmış olacağım.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'By the end of this month, she ___ 50 international flights.',
          options: ['will fly', 'will be flying', 'will have flown', 'is flying'],
          correctIndex: 2,
          explanation: 'Gelecekte tamamlanmış olacak eylem için "will have flown" kullanılır.'
        }
      ]
    }
  },

  'C1-U1-L5': {
    id: 'C1-U1-L5',
    title: 'Uygulama: Profesyonel Anonslar',
    type: 'practice',
    content: {
      sections: [
        {
          type: 'scenario',
          title: '✈️ İleri Seviye Anons Örnekleri',
          dialogues: [
            {
              speaker: 'Kaptan',
              text: 'Ladies and gentlemen, we have been cruising at 35,000 feet for the past two hours.',
              translation: 'Son iki saattir 35.000 feet\'te seyir halindeyiz.'
            },
            {
              speaker: 'Kabin Amiri',
              text: 'By the time we land, we will have been in the air for 12 hours.',
              translation: 'İndiğimizde 12 saattir havada olmuş olacağız.'
            },
            {
              speaker: 'Kaptan',
              text: 'We had been expecting turbulence, but conditions have improved.',
              translation: 'Türbülans bekliyorduk ama koşullar iyileşti.'
            }
          ]
        }
      ],
      exercises: [
        {
          type: 'fillBlank',
          sentences: [
            { text: 'By arrival, we ___ ___ ___ (serve) over 200 meals.', answer: 'will have served' },
            { text: 'The crew ___ ___ ___ (work) for 10 hours when we land.', answer: 'will have been working' }
          ]
        }
      ]
    }
  },

  'C1-U1-EXAM': {
    id: 'C1-U1-EXAM',
    title: '📝 Ünite 1 Sınavı',
    type: 'exam',
    content: {
      timeLimit: 20,
      passingScore: 80,
      questions: [
        {
          type: 'multipleChoice',
          question: 'I ___ as a flight attendant for 15 years now.',
          options: ['work', 'have worked', 'have been working', 'am working'],
          correctIndex: 2
        },
        {
          type: 'multipleChoice',
          question: 'By the time they arrived, the plane ___ .',
          options: ['departed', 'had departed', 'has departed', 'was departing'],
          correctIndex: 1
        },
        {
          type: 'multipleChoice',
          question: 'By next month, she ___ 100 flights.',
          options: ['will complete', 'will have completed', 'completes', 'has completed'],
          correctIndex: 1
        }
      ]
    }
  }
};

export default C1_UNIT_1;
